import React, { useState, useEffect } from 'react';
import showToast from './Toast';
import { 
  FiMenu, 
  FiX, 
  FiSave, 
  FiRotateCcw, 
  FiMove,
  FiCheck,
  FiSettings
} from 'react-icons/fi';
import axiosInstance from '../AxiosInstance';
import config from '../config/frontend.config';
import './MenuOrderManager.css';

const MenuOrderManager = ({ isOpen, onClose, onSave, currentMenuItems }) => {
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);

  useEffect(() => {
    if (isOpen && currentMenuItems) {
      setMenuItems([...currentMenuItems]);
    }
  }, [isOpen, currentMenuItems]);

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedItem === null || draggedItem === dropIndex) return;

    const newMenuItems = [...menuItems];
    const draggedItemData = newMenuItems[draggedItem];
    
    // Remove dragged item from original position
    newMenuItems.splice(draggedItem, 1);
    
    // Insert dragged item at new position
    newMenuItems.splice(dropIndex, 0, draggedItemData);
    
    setMenuItems(newMenuItems);
    setDraggedItem(null);
  };

  const moveItem = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    
    const newMenuItems = [...menuItems];
    const item = newMenuItems.splice(fromIndex, 1)[0];
    newMenuItems.splice(toIndex, 0, item);
    setMenuItems(newMenuItems);
  };

  const saveMenuOrder = async () => {
    try {
      setSaving(true);
      const userId = config.getUserId();
      
      if (!userId) {
        showToast.error('User not authenticated');
        return;
      }

      const menuOrder = menuItems.map(item => item.path);
      
      const response = await axiosInstance.put(
        `${config.api.baseURL}/${config.api.apiPath}/user/menu-preferences/${userId}`,
        { menuOrder }
      );

      if (response.data.statusCode === 200) {
        showToast.success('Menu order saved successfully!');
        onSave(menuItems);
        onClose();
      } else {
        showToast.error('Failed to save menu order');
      }
    } catch (error) {
      console.error('Error saving menu order:', error);
      showToast.error('Failed to save menu order');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = async () => {
    try {
      setLoading(true);
      const userId = config.getUserId();
      
      if (!userId) {
        showToast.error('User not authenticated');
        return;
      }

      const response = await axiosInstance.delete(
        `${config.api.baseURL}/${config.api.apiPath}/user/menu-preferences/${userId}`
      );

      if (response.data.statusCode === 200) {
        // Clear localStorage
        localStorage.removeItem('menuOrder');
        showToast.success('Menu order reset to default!');
        onSave(currentMenuItems);
        onClose();
      } else {
        showToast.error('Failed to reset menu order');
      }
    } catch (error) {
      console.error('Error resetting menu order:', error);
      showToast.error('Failed to reset menu order');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="menu-order-overlay">
      <div className="menu-order-modal">
        <div className="menu-order-header">
          <div className="menu-order-title">
            <FiSettings className="menu-order-icon" />
            <h3>Customize Menu Order</h3>
          </div>
          <button 
            className="menu-order-close"
            onClick={onClose}
            disabled={saving}
          >
            <FiX size={20} />
          </button>
        </div>

        <div className="menu-order-content">
          <p className="menu-order-description">
            Drag and drop menu items to reorder them. The first items will appear at the top of your sidebar.
          </p>

          <div className="menu-items-list">
            {menuItems.map((item, index) => (
              <div
                key={`${item.path}-${index}`}
                className={`menu-item ${draggedItem === index ? 'dragging' : ''}`}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, index)}
              >
                <div className="menu-item-drag-handle">
                  <FiMove size={16} />
                </div>
                <div className="menu-item-icon">
                  {item.icon}
                </div>
                <div className="menu-item-text">
                  {item.text}
                </div>
                <div className="menu-item-actions">
                  {index > 0 && (
                    <button
                      className="menu-item-action-btn"
                      onClick={() => moveItem(index, index - 1)}
                      title="Move Up"
                    >
                      ↑
                    </button>
                  )}
                  {index < menuItems.length - 1 && (
                    <button
                      className="menu-item-action-btn"
                      onClick={() => moveItem(index, index + 1)}
                      title="Move Down"
                    >
                      ↓
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="menu-order-footer">
          <button
            className="menu-order-btn menu-order-btn-secondary"
            onClick={resetToDefault}
            disabled={loading || saving}
          >
            <FiRotateCcw size={16} />
            Reset to Default
          </button>
          <div className="menu-order-btn-group">
            <button
              className="menu-order-btn menu-order-btn-secondary"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              className="menu-order-btn menu-order-btn-primary"
              onClick={saveMenuOrder}
              disabled={saving}
            >
              {saving ? (
                <>
                  <div className="loading-spinner" />
                  Saving...
                </>
              ) : (
                <>
                  <FiSave size={16} />
                  Save Order
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MenuOrderManager;
